import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';
import './CommunityNewsPage.css';

const CommunityNewsPage = () => {
  const { currentUser } = useContext(AuthContext);
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalPosts: 0,
    hasNext: false,
    hasPrev: false
  });
  const [view, setView] = useState('list'); // 'list' or 'detail'
  const [sortBy, setSortBy] = useState('publishDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchTags();
    fetchPosts();
  }, [selectedTags, searchTerm, pagination.currentPage, sortBy, sortOrder, priorityFilter]);

  const fetchTags = async () => {
    try {
      const response = await api.get('/api/community-news/tags');
      setTags(response.data.tags);
    } catch (err) {
      console.error('Error fetching tags:', err);
    }
  };

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.currentPage,
        limit: 10,
        sortBy,
        sortOrder
      });

      if (selectedTags.length > 0) {
        params.append('tags', selectedTags.join(','));
      }
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (priorityFilter) {
        params.append('priority', priorityFilter);
      }

      const response = await api.get(`/api/community-news/public?${params}`);
      setPosts(response.data.posts);
      setPagination(response.data.pagination);
      setError('');
    } catch (err) {
      console.error('Error fetching community news:', err);
      setError('Failed to load community news');
    } finally {
      setLoading(false);
    }
  };

  const fetchPostDetail = async (postId) => {
    try {
      setLoading(true);
      const response = await api.get(`/api/community-news/public/${postId}`);
      setSelectedPost(response.data.post);
      setView('detail');
      setError('');
    } catch (err) {
      console.error('Error fetching post detail:', err);
      setError('Failed to load post details');
    } finally {
      setLoading(false);
    }
  };

  const handleTagToggle = (tag) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    fetchPosts();
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  const toggleLike = async (postId) => {
    if (!currentUser) {
      setError('Please log in to like posts');
      return;
    }

    try {
      await api.post(`/api/community-news/${postId}/like`);
      fetchPosts(); // Refresh posts to get updated like count
    } catch (err) {
      console.error('Error toggling like:', err);
      setError('Failed to update like');
    }
  };

  const addComment = async (postId, content) => {
    if (!currentUser) {
      setError('Please log in to comment');
      return;
    }

    if (!content.trim()) {
      setError('Comment cannot be empty');
      return;
    }

    try {
      await api.post(`/api/community-news/${postId}/comment`, { content });
      if (selectedPost && selectedPost._id === postId) {
        fetchPostDetail(postId); // Refresh post detail if viewing
      }
      setError('');
    } catch (err) {
      console.error('Error adding comment:', err);
      setError('Failed to add comment');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getPriorityBadgeClass = (priority) => {
    switch (priority) {
      case 'urgent': return 'priority-urgent';
      case 'high': return 'priority-high';
      case 'normal': return 'priority-normal';
      case 'low': return 'priority-low';
      default: return 'priority-normal';
    }
  };

  const renderPostList = () => (
    <div className="posts-container">
      {posts.map(post => (
        <div key={post._id} className="post-card">
          <div className="post-header">
            <h3 
              className="post-title"
              onClick={() => fetchPostDetail(post._id)}
            >
              {post.title}
            </h3>
            <div className="post-meta">
              <span className={`priority-badge ${getPriorityBadgeClass(post.priority)}`}>
                {post.priority.toUpperCase()}
              </span>
              <span className="post-date">{formatDate(post.publishDate)}</span>
            </div>
          </div>
          
          {post.featuredImage && (
            <img 
              src={post.featuredImage} 
              alt={post.title}
              className="post-featured-image"
            />
          )}
          
          <p className="post-excerpt" dangerouslySetInnerHTML={{ __html: post.excerpt }} />
          
          <div className="post-tags">
            {post.tags.map(tag => (
              <span key={tag} className="tag">{tag}</span>
            ))}
          </div>
          
          <div className="post-footer">
            <div className="post-stats">
              <span className="view-count">👁️ {post.viewCount}</span>
              <span className="like-count">❤️ {post.likes?.length || 0}</span>
              <span className="comment-count">💬 {post.comments?.length || 0}</span>
            </div>
            
            <div className="post-actions">
              {currentUser && (
                <button 
                  className="like-btn"
                  onClick={() => toggleLike(post._id)}
                >
                  {post.likes?.some(like => like.userId === currentUser.id) ? '❤️' : '🤍'} Like
                </button>
              )}
              <button 
                className="read-more-btn"
                onClick={() => fetchPostDetail(post._id)}
              >
                Read More
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderPostDetail = () => {
    if (!selectedPost) return null;

    return (
      <div className="post-detail">
        <button 
          className="back-btn"
          onClick={() => {
            setView('list');
            setSelectedPost(null);
          }}
        >
          ← Back to News
        </button>
        
        <article className="post-detail-content">
          <header className="post-detail-header">
            <h1>{selectedPost.title}</h1>
            <div className="post-detail-meta">
              <span className={`priority-badge ${getPriorityBadgeClass(selectedPost.priority)}`}>
                {selectedPost.priority.toUpperCase()}
              </span>
              <span>By {selectedPost.author.name}</span>
              <span>{formatDate(selectedPost.publishDate)}</span>
            </div>
          </header>
          
          {selectedPost.featuredImage && (
            <img 
              src={selectedPost.featuredImage} 
              alt={selectedPost.title}
              className="post-detail-image"
            />
          )}
          
          <div 
            className="post-content"
            dangerouslySetInnerHTML={{ __html: selectedPost.content }}
          />
          
          <div className="post-tags">
            {selectedPost.tags.map(tag => (
              <span key={tag} className="tag">{tag}</span>
            ))}
          </div>
          
          <div className="post-interactions">
            <div className="post-stats">
              <span>👁️ {selectedPost.viewCount} views</span>
              <span>❤️ {selectedPost.likes?.length || 0} likes</span>
            </div>
            
            {currentUser && (
              <button 
                className="like-btn"
                onClick={() => toggleLike(selectedPost._id)}
              >
                {selectedPost.likes?.some(like => like.userId === currentUser.id) ? '❤️' : '🤍'} Like
              </button>
            )}
          </div>
          
          <CommentsSection 
            post={selectedPost}
            currentUser={currentUser}
            onAddComment={addComment}
          />
        </article>
      </div>
    );
  };

  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;

    return (
      <div className="pagination">
        <button 
          disabled={!pagination.hasPrev}
          onClick={() => handlePageChange(pagination.currentPage - 1)}
        >
          Previous
        </button>
        
        <span className="page-info">
          Page {pagination.currentPage} of {pagination.totalPages}
        </span>
        
        <button 
          disabled={!pagination.hasNext}
          onClick={() => handlePageChange(pagination.currentPage + 1)}
        >
          Next
        </button>
      </div>
    );
  };

  if (loading && posts.length === 0) {
    return (
      <div className="community-news-page">
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading community news...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="community-news-page">
      <header className="page-header">
        <h1>Community News</h1>
        <p>Stay updated with the latest community announcements and news</p>
      </header>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {view === 'list' && (
        <>
          <div className="filters-section">
            {/* Search and Filter Bar */}
            <div className="top-controls">
              <form onSubmit={handleSearch} className="search-form">
                <input
                  type="text"
                  placeholder="Search news..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                <button type="submit" className="search-btn">🔍</button>
              </form>

              <div className="filter-actions">
                <button 
                  className={`filter-toggle ${showFilters ? 'active' : ''}`}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <span className="filter-icon">⚙️</span>
                  Filters
                  {selectedTags.length > 0 && (
                    <span className="active-filter-count">{selectedTags.length}</span>
                  )}
                </button>

                <select 
                  value={`${sortBy}-${sortOrder}`} 
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setSortBy(field);
                    setSortOrder(order);
                  }}
                  className="sort-filter"
                >
                  <option value="publishDate-desc">📅 Newest First</option>
                  <option value="publishDate-asc">📅 Oldest First</option>
                  <option value="title-asc">🔤 Title A-Z</option>
                  <option value="title-desc">🔤 Title Z-A</option>
                  <option value="priority-desc">⚡ Priority High-Low</option>
                </select>
              </div>
            </div>

            {/* Expandable Filters Panel */}
            {showFilters && (
              <div className="filters-panel">
                <div className="filter-group">
                  <label className="filter-label">
                    <span className="filter-icon">⚡</span>
                    Priority Level
                  </label>
                  <select 
                    value={priorityFilter} 
                    onChange={(e) => setPriorityFilter(e.target.value)}
                    className="priority-filter"
                  >
                    <option value="">All Priorities</option>
                    <option value="urgent">🔴 Urgent</option>
                    <option value="high">🟡 High</option>
                    <option value="normal">🟢 Normal</option>
                    <option value="low">🔵 Low</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label className="filter-label">
                    <span className="filter-icon">🏷️</span>
                    Categories
                    {selectedTags.length > 0 && (
                      <button 
                        className="clear-tags"
                        onClick={() => setSelectedTags([])}
                      >
                        Clear All
                      </button>
                    )}
                  </label>
                  <div className="tags-grid">
                    {tags.sort().map(tag => (
                      <button
                        key={tag}
                        className={`tag-filter ${selectedTags.includes(tag) ? 'active' : ''}`}
                        onClick={() => handleTagToggle(tag)}
                      >
                        <span className="tag-name">{tag}</span>
                        {selectedTags.includes(tag) && <span className="tag-check">✓</span>}
                      </button>
                    ))}
                  </div>
                </div>

                {(selectedTags.length > 0 || priorityFilter) && (
                  <div className="active-filters">
                    <span className="active-filters-label">Active filters:</span>
                    {priorityFilter && (
                      <span className="active-filter priority">
                        Priority: {priorityFilter}
                        <button onClick={() => setPriorityFilter('')}>×</button>
                      </span>
                    )}
                    {selectedTags.map(tag => (
                      <span key={tag} className="active-filter tag">
                        {tag}
                        <button onClick={() => handleTagToggle(tag)}>×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {renderPostList()}
          {renderPagination()}
        </>
      )}

      {view === 'detail' && renderPostDetail()}
    </div>
  );
};

// Comments Section Component
const CommentsSection = ({ post, currentUser, onAddComment }) => {
  const [newComment, setNewComment] = useState('');
  const [showComments, setShowComments] = useState(true);

  const handleSubmitComment = (e) => {
    e.preventDefault();
    if (newComment.trim()) {
      onAddComment(post._id, newComment);
      setNewComment('');
    }
  };

  return (
    <div className="comments-section">
      <div className="comments-header">
        <h3>Comments ({post.comments?.length || 0})</h3>
        <button 
          className="toggle-comments"
          onClick={() => setShowComments(!showComments)}
        >
          {showComments ? 'Hide' : 'Show'} Comments
        </button>
      </div>

      {showComments && (
        <>
          {currentUser && (
            <form onSubmit={handleSubmitComment} className="comment-form">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                rows="3"
                maxLength="1000"
                className="comment-textarea"
              />
              <button type="submit" className="submit-comment-btn">
                Post Comment
              </button>
            </form>
          )}

          <div className="comments-list">
            {post.comments?.map((comment, index) => (
              <div key={index} className="comment">
                <div className="comment-header">
                  <strong>{comment.userName}</strong>
                  <span className="comment-date">
                    {new Date(comment.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <p className="comment-content">{comment.content}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default CommunityNewsPage;